import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { productScoresKeys } from "@/application/query-keys/product-scores.keys";
import {
  productScoresRepository,
  type UpsertProductScoreInput,
} from "@/infrastructure/repositories/product-scores.supabase.repository";
import type { ProductScoreRow } from "@/infrastructure/supabase/database.types";

export function useAllProductScores() {
  return useQuery({
    queryKey: productScoresKeys.list(),
    queryFn: () => productScoresRepository.list(),
  });
}

export function useProductScores(productId: string | undefined) {
  return useQuery({
    queryKey: productScoresKeys.byProduct(productId ?? ""),
    queryFn: () =>
      productId ? productScoresRepository.listByProduct(productId) : Promise.resolve([]),
    enabled: typeof productId === "string" && productId.length > 0,
  });
}

export function useUpsertProductScore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertProductScoreInput) => productScoresRepository.upsert(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: productScoresKeys.all });
      const listKey = productScoresKeys.list();
      const byProductKey = productScoresKeys.byProduct(input.product_id);
      const previousList = qc.getQueryData<ProductScoreRow[]>(listKey);
      const previousByProduct = qc.getQueryData<ProductScoreRow[]>(byProductKey);

      const now = new Date().toISOString();
      const optimisticRow: ProductScoreRow = {
        id: `optimistic-${input.product_id}-${input.criterion_id}`,
        product_id: input.product_id,
        criterion_id: input.criterion_id,
        score: input.score,
        dealbreaker_fail: input.dealbreaker_fail,
        evidence_md: input.evidence_md,
        vibes_revisit: input.vibes_revisit,
        created_at: now,
        updated_at: now,
      };

      const replace = (current: ProductScoreRow[] | undefined) => {
        const base = current ?? [];
        const filtered = base.filter(
          (s) => !(s.product_id === input.product_id && s.criterion_id === input.criterion_id)
        );
        return [optimisticRow, ...filtered];
      };
      qc.setQueryData<ProductScoreRow[]>(listKey, replace);
      qc.setQueryData<ProductScoreRow[]>(byProductKey, replace);

      return { previousList, previousByProduct };
    },
    onError: (_err, input, ctx) => {
      if (ctx?.previousList) {
        qc.setQueryData(productScoresKeys.list(), ctx.previousList);
      }
      if (ctx?.previousByProduct) {
        qc.setQueryData(productScoresKeys.byProduct(input.product_id), ctx.previousByProduct);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: productScoresKeys.all });
    },
  });
}
